import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useIsAuthenticated } from 'react-auth-kit';
import { useCartContext } from 'context/CartContext/CartContext';
import { useAuthCode } from 'context/AuthCodeContext/AuthCodeContext';
import { usePopup } from 'context/PopupContext/PopupContext';
import { useCheckoutMutation } from './useCheckoutMutation';
import { sendOrder, sendOrderNumber } from 'api/QiPaymentOrders';
import { sendOrderSuperQi } from 'api/AlipayPaymentOrder';
import { captureException } from '@sentry/react';
import type { RudderAnalytics } from '@rudderstack/analytics-js';
import { withTokenRefresh } from 'utils/withTokenRefresh';
import useHylideBridge from 'hooks/useHylideBridge';
import { useEventTracking } from 'hooks/useEventTracking';
import type { FormValues } from '../TicketHolderForm/TicketHolderForm';
import type { CalculatePriceResponse, CheckoutResponse } from 'api/responseTypes';
import type { CheckoutRequest } from 'api/requestTypes';
import { transformTicketHoldersData } from '../utils/transformTicketHoldersData';
import { ROUTES } from 'router/routes';

type TicketHoldersPayload = ReturnType<typeof transformTicketHoldersData>;

interface MiniProgramPaymentResult {
  resultCode: string | number;
}

const isClientErrorPayload = (value: CheckoutResponse & { status?: number }): boolean =>
  value.status !== undefined && value.status >= 400 && value.status < 500;

const hasValidOrderNumber = (value: unknown): value is CheckoutResponse => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.orderNumber === 'string' && record.orderNumber.length > 0;
};

const toPopupErrorMessage = (error: unknown, fallback: string): string => {
  const fromAxios = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (typeof fromAxios === 'string' && fromAxios.length > 0) return fromAxios;
  if (error instanceof Error && error.message.length > 0) return error.message;
  return fallback;
};

const normalizeResultCode = (code: string | number | undefined | null): string => {
  if (code === undefined || code === null) return '';
  return String(code);
};

/** One event per (order, event kind, result code); avoids double analytics from bridge retries. */
const miniProgramTradePayTrackedKeys = new Set<string>();

const tradePayTrackingDedupeKey = (
  orderNumber: string,
  kind: 'order_completed' | 'order_cancelled' | 'order_failed',
  outcomeCode: string,
): string => `${orderNumber}\0${kind}\0${outcomeCode}`;

const withTradePayTrackingOnce = (
  dedupeKey: string,
  emit: () => void,
  logMessage: string,
): void => {
  if (miniProgramTradePayTrackedKeys.has(dedupeKey)) return;
  miniProgramTradePayTrackedKeys.add(dedupeKey);
  try {
    emit();
  } catch (trackingError) {
    miniProgramTradePayTrackedKeys.delete(dedupeKey);
    console.error(logMessage, trackingError);
  }
};

const extractResultCodeFromTradePayFailure = (err: unknown): string => {
  if (err && typeof err === 'object') {
    const record = err as Record<string, unknown>;
    if (record.resultCode != null) return String(record.resultCode);
    if (record.code != null) return String(record.code);
  }
  if (typeof err === 'string' && err.length > 0) return err;
  return '';
};

const MINI_PROGRAM_PAYMENT_FAILED = '4000';
const MINI_PROGRAM_CANCELLED = '6001';
const MINI_PROGRAM_SUCCESS = '9000';
const MINI_PROGRAM_TRADEPAY_BRIDGE_FAIL = 'MINI_PROGRAM_TRADEPAY_FAIL';

export const useCheckoutSubmission = (
  priceCalculation: CalculatePriceResponse | null,
  onAuthRequired: () => void,
  requireAuthentication = true,
) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const isAuthenticated = useIsAuthenticated();
  const { showPopup } = usePopup();
  const { tickets, holdToken, eventId, promoCode, invitationId } = useCartContext();
  const { userToken, authCode } = useAuthCode();
  const { mutate } = useCheckoutMutation();
  const hylideBridge = useHylideBridge();
  const { eventTracking } = useEventTracking();

  const getSessionId = (): number | undefined =>
    (window as Window & { rudderanalytics?: RudderAnalytics }).rudderanalytics?.getSessionId?.() ??
    undefined;
  const [isLoading, setIsLoading] = useState(false);

  const genericErrorMessage = formatMessage({ id: 'global.error.generictitle' });

  const navigateToOrderSummary = (orderNumber: string) => {
    navigate(`/${ROUTES.summary}?CartID=${encodeURIComponent(orderNumber)}`);
  };

  const handleQiPayment = async (
    holdTokenValue: string,
    ticketHoldersData: TicketHoldersPayload,
    calculatedPrice: CalculatePriceResponse,
  ) => {
    if (!eventId) {
      showPopup({
        message: formatMessage({ id: 'checkout.eventInfoMissing' }),
        type: 'ERROR',
      });
      setIsLoading(false);
      return;
    }

    try {
      const response = await withTokenRefresh(() =>
        sendOrder({
          eventInfoId: 1,
          eventId,
          holdToken: holdTokenValue,
          tickets: ticketHoldersData,
          paymentMethod: 'QI_SERVICES_PAYMENT_GATEWAY',
          ...(promoCode && { promoCode }),
          ...(invitationId && { invitationId }),
          sessionId: getSessionId(),
        }),
      );

      const responseWithOptionalStatus = response as CheckoutResponse & {
        status?: number;
        message?: string;
      };

      if (isClientErrorPayload(responseWithOptionalStatus)) {
        const message = responseWithOptionalStatus.message || genericErrorMessage;
        showPopup({ message, type: 'ERROR' });
        captureException(new Error(message));
        return;
      }

      if (!hasValidOrderNumber(response)) {
        showPopup({
          message: formatMessage({ id: 'checkout.orderNotConfirmed' }),
          type: 'ERROR',
        });
        captureException(new Error('Checkout response missing orderNumber'));
        return;
      }

      const { orderNumber, paymentLink } = response;

      if (!paymentLink) {
        navigateToOrderSummary(orderNumber);
        return;
      }

      window.qi.purchase(
        {
          price: calculatedPrice.total,
          description: '',
          timeToLiveMillis: 60000,
          merchantTransactionID: orderNumber,
        },
        async () => {
          sendOrderNumber({ order_number: orderNumber }).then((res: unknown) => {
            if (res) {
              showPopup({
                message: res as string,
                type: 'SUCCESS',
              });
              navigate('/');
            }
          });
        },
      );
    } catch (error: unknown) {
      showPopup({
        message: toPopupErrorMessage(error, genericErrorMessage),
        type: 'ERROR',
      });
      captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuperQiPayment = async (
    holdTokenValue: string,
    ticketHoldersData: TicketHoldersPayload,
    checkoutTotalPrice: number,
  ) => {
    if (!hylideBridge) {
      setIsLoading(false);
      return;
    }

    if (!eventId) {
      showPopup({
        message: formatMessage({ id: 'checkout.eventInfoMissing' }),
        type: 'ERROR',
      });
      setIsLoading(false);
      return;
    }

    try {
      const response = await sendOrderSuperQi({
        eventId,
        eventInfoId: 0,
        holdToken: holdTokenValue,
        tickets: ticketHoldersData,
        paymentMethod: 'MINI_PROGRAM',
        ...(promoCode && { promoCode }),
        ...(invitationId && { invitationId }),
        sessionId: getSessionId(),
      });

      if (!hasValidOrderNumber(response)) {
        showPopup({
          message: formatMessage({ id: 'checkout.orderNotConfirmed' }),
          type: 'ERROR',
        });
        captureException(new Error('Mini program checkout missing orderNumber'));
        return;
      }

      const { createdId, orderNumber, paymentLink } = response;

      if (!paymentLink) {
        navigateToOrderSummary(orderNumber);
        return;
      }

      if (!authCode) return;

      hylideBridge.tradePay({
        paymentUrl: paymentLink,
        success: (result: MiniProgramPaymentResult) => {
          const code = normalizeResultCode(result.resultCode);

          if (code === MINI_PROGRAM_CANCELLED) {
            withTradePayTrackingOnce(
              tradePayTrackingDedupeKey(orderNumber, 'order_cancelled', MINI_PROGRAM_CANCELLED),
              () =>
                eventTracking.order.cancelled({
                  orderId: createdId,
                  eventId: eventId ?? 0,
                  totalPrice: checkoutTotalPrice,
                  orderStatus: 'CANCELLED',
                  errorCode: MINI_PROGRAM_CANCELLED,
                }),
              'Failed to track mini app payment cancelled:',
            );
            showPopup({
              message: formatMessage({ id: 'checkout.miniProgram.paymentCancelled' }),
              type: 'INFO',
            });
            return;
          }

          if (code === MINI_PROGRAM_SUCCESS) {
            withTradePayTrackingOnce(
              tradePayTrackingDedupeKey(orderNumber, 'order_completed', MINI_PROGRAM_SUCCESS),
              () =>
                eventTracking.order.completed({
                  orderId: createdId,
                  eventId: eventId ?? 0,
                  totalPrice: checkoutTotalPrice,
                }),
              'Failed to track mini app checkout completion:',
            );
            showPopup({
              message: formatMessage({ id: 'checkout.miniProgram.paymentSuccess' }),
              type: 'SUCCESS',
            });
            navigate('/');
            return;
          }

          if (code === MINI_PROGRAM_PAYMENT_FAILED) {
            withTradePayTrackingOnce(
              tradePayTrackingDedupeKey(orderNumber, 'order_failed', MINI_PROGRAM_PAYMENT_FAILED),
              () =>
                eventTracking.order.failed({
                  orderId: createdId,
                  eventId: eventId ?? 0,
                  totalPrice: checkoutTotalPrice,
                  orderStatus: 'FAILURE',
                  errorCode: MINI_PROGRAM_PAYMENT_FAILED,
                }),
              'Failed to track mini app payment failure:',
            );
            showPopup({
              message: formatMessage({ id: 'checkout.miniProgram.paymentFailed4000' }),
              type: 'ERROR',
            });
            captureException(new Error('Mini program payment failed (4000)'));
            return;
          }
        },
        fail: (err: unknown) => {
          const code = extractResultCodeFromTradePayFailure(err);
          const resolvedEventId = eventId ?? 0;

          if (code === MINI_PROGRAM_CANCELLED) {
            withTradePayTrackingOnce(
              tradePayTrackingDedupeKey(orderNumber, 'order_cancelled', MINI_PROGRAM_CANCELLED),
              () =>
                eventTracking.order.cancelled({
                  orderId: createdId,
                  eventId: resolvedEventId,
                  totalPrice: checkoutTotalPrice,
                  orderStatus: 'CANCELLED',
                  errorCode: MINI_PROGRAM_CANCELLED,
                }),
              'Failed to track mini app payment cancelled:',
            );
          } else if (code === MINI_PROGRAM_PAYMENT_FAILED) {
            withTradePayTrackingOnce(
              tradePayTrackingDedupeKey(orderNumber, 'order_failed', MINI_PROGRAM_PAYMENT_FAILED),
              () =>
                eventTracking.order.failed({
                  orderId: createdId,
                  eventId: resolvedEventId,
                  totalPrice: checkoutTotalPrice,
                  orderStatus: 'FAILURE',
                  errorCode: MINI_PROGRAM_PAYMENT_FAILED,
                }),
              'Failed to track mini app payment failure:',
            );
          } else {
            const failureCode = code || MINI_PROGRAM_TRADEPAY_BRIDGE_FAIL;
            withTradePayTrackingOnce(
              tradePayTrackingDedupeKey(orderNumber, 'order_failed', failureCode),
              () =>
                eventTracking.order.failed({
                  orderId: createdId,
                  eventId: resolvedEventId,
                  totalPrice: checkoutTotalPrice,
                  orderStatus: 'FAILURE',
                  errorCode: failureCode,
                }),
              'Failed to track mini app tradePay failure:',
            );
          }

          showPopup({
            message: formatMessage({ id: 'checkout.miniProgram.paymentFailed' }),
            type: 'ERROR',
          });
          captureException(new Error('Mini program tradePay failed'));
        },
      });
    } catch (error: unknown) {
      captureException(error);
      showPopup({
        message: toPopupErrorMessage(error, genericErrorMessage),
        type: 'ERROR',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    if (isLoading) {
      return;
    }

    const holdTokenValue = holdToken ?? 'empty';
    const ticketHoldersData = transformTicketHoldersData(data, tickets);

    // TODO(TZ-CO-6 write half): `data.ticketHolders[i]` also carries `source` and `saveDetails`,
    // which are intentionally not on the wire yet — holder attachment and the `saved_holder`
    // write/dedupe are part of the deferred BE submit contract. Once it lands, attach them here
    // rather than re-deriving intent further down. `source` is also the payload TZ-CO-11 reports.

    // Checkout depends on a successful price calculation first.
    if (!priceCalculation) {
      const isAnonymousOnWeb = !userToken && authCode === null;
      const mustSignInBeforeContinuing = isAnonymousOnWeb && !isAuthenticated();

      if (mustSignInBeforeContinuing) {
        onAuthRequired();
        return;
      }

      showPopup({
        message: formatMessage({ id: 'checkout.waitForPriceCalculation' }),
        type: 'ERROR',
      });
      return;
    }

    setIsLoading(true);

    // Browser checkout without app auth: guest flow uses React Query `mutate`.
    const isWebGuestCheckout = !userToken && authCode === null;
    if (isWebGuestCheckout) {
      if (requireAuthentication && !isAuthenticated()) {
        onAuthRequired();
        setIsLoading(false);
        return;
      }

      const hasTicketHolders = Boolean(data?.ticketHolders?.length);
      const canStartGuestCheckout = hasTicketHolders && Boolean(eventId);
      if (!canStartGuestCheckout) {
        setIsLoading(false);
        return;
      }

      const guestCheckoutBody: CheckoutRequest = {
        eventInfoId: 1,
        eventId,
        holdToken: holdTokenValue,
        tickets: ticketHoldersData,
        ...(promoCode && { promoCode }),
        ...(invitationId && { invitationId }),
        sessionId: getSessionId(),
      };

      mutate(guestCheckoutBody, {
        onSettled: () => setIsLoading(false),
      });
      return;
    }

    // Authenticated web user: Qi payment gateway.
    if (userToken) {
      await handleQiPayment(holdTokenValue, ticketHoldersData, priceCalculation);
      return;
    }

    // Mini-program context (authCode set): Super Qi / native bridge.
    await handleSuperQiPayment(holdTokenValue, ticketHoldersData, priceCalculation.total);
  };

  return {
    handleSubmit,
    isLoading,
  };
};
